/**
 * **종류마다 다섯** (2026-09-15, Chris: 「프롭들 전체 종류마다 한 다섯 개씩은 있어야」).
 * 새 조각을 그리지 않고 기존 조각의 dims(수종·다리·크기·칸 수·천)를 프리셋으로 굳혀 카탈로그에 따로 올린다.
 * 에디터에서 「원탁 · 흰 칠」을 바로 집을 수 있고, 배치 파일에서는 `piece: 'round_table_white'` 로 쓴다.
 * build 는 원본을 그대로 부르므로 원본을 고치면 다섯이 같이 고쳐진다.
 */
import type { Piece } from './pieces';
import { MANOR_PIECES } from './pieces';
import { SAHOL_STORY_PIECES } from './saholStory';
import { SAHOL_DEVICE_PIECES } from './saholDevices';
import { MeshWriter } from './kernel';

const BASES = [...MANOR_PIECES, ...SAHOL_STORY_PIECES, ...SAHOL_DEVICE_PIECES];
function variant(baseId: string, id: string, name: string, dims: Record<string, number>): Piece {
  const base = BASES.find((p) => p.id === baseId);
  if (!base) throw new Error(`variant: base piece '${baseId}' not found`);
  const v: Piece = { ...base, id, name, dims: { ...base.dims, ...dims }, variantOf: baseId };
  /** openable 을 물려받되 변형 치수로 실제로 지어지는 부품만 남긴다 —
   *  칸 수가 줄어 '4칸 책'·'4단' 같은 부품이 안 만들어지면 허공에 붙는 빈 프롭이 생긴다 (2026-09-16) */
  if (v.openable?.length) {
    const w = new MeshWriter();
    v.build(w, { ...v.dims });
    w.build();
    const have = new Set(w.buildMovables().keys());
    v.openable = v.openable.filter((o) => o.node || have.has(o.part));
    if (!v.openable.length) v.openable = undefined;
  }
  return v;
}

export const VARIANT_PIECES: Piece[] = [
  variant('round_table', 'round_table_4leg', '원탁 · 다리 넷', { legs: 4 }),
  variant('round_table', 'round_table_white', '원탁 · 흰 칠', { wood: 3, legs: 4 }),
  variant('round_table', 'round_table_small', '원탁 · 작은 것', { r: 0.4, h: 0.72 }),
  variant('round_table', 'round_table_low', '낮은 원탁(티테이블)', { r: 0.5, h: 0.45 }),
  variant('dining_table', 'dining_table_turned', '식탁 · 선반 깎은 다리', { turned: 1, wood: 0 }),
  variant('dining_table', 'dining_table_long', '긴 식탁', { w: 2.2 }),
  variant('dining_table', 'dining_table_small', '작은 식탁', { w: 1.2, d: 0.8 }),
  variant('dining_table', 'dining_table_dark', '식탁 · 흑단', { wood: 2 }),
  variant('desk', 'desk_plain', '책상 · 참나무 직선 다리', { wood: 1, plain: 1 }),
  variant('desk', 'desk_white', '책상 · 흰 칠', { wood: 3, plain: 1 }),
  variant('desk', 'desk_dark', '책상 · 흑단', { wood: 2 }),
  variant('desk', 'desk_wide', '넓은 책상', { w: 1.8 }),
  variant('chair', 'chair_white', '의자 · 흰 칠', { wood: 3 }),
  variant('chair', 'chair_dark', '의자 · 흑단', { wood: 2 }),
  variant('chair', 'chair_ladder', '의자 · 사다리 등받이', { slats: 3 }),
  variant('chair', 'chair_walnut', '의자 · 호두', { wood: 0 }),
  variant('bookshelf', 'bookshelf_low', '낮은 책장', { h: 1.2, shelves: 3 }),
  variant('bookshelf', 'bookshelf_wide', '넓은 책장', { w: 1.4 }),
  variant('bookshelf', 'bookshelf_white', '책장 · 흰 칠', { wood: 3 }),
  variant('bookshelf', 'bookshelf_dark', '책장 · 흑단', { wood: 2 }),
  variant('bed', 'bed_single', '싱글 침대', { w: 1.0 }),
  variant('bed', 'bed_white', '침대 · 흰 칠', { wood: 3 }),
  variant('bed', 'bed_dark', '침대 · 흑단', { wood: 2 }),
  variant('bed', 'bed_oak', '침대 · 참나무', { wood: 1 }),
  variant('nightstand', 'nightstand_white', '협탁 · 흰 칠', { wood: 3 }),
  variant('nightstand', 'nightstand_dark', '협탁 · 흑단', { wood: 2 }),
  variant('nightstand', 'nightstand_oak', '협탁 · 참나무', { wood: 1 }),
  variant('nightstand', 'nightstand_tall', '높은 협탁', { h: 0.72 }),
  variant('wardrobe', 'wardrobe_white', '옷장 · 흰 칠', { wood: 3 }),
  variant('wardrobe', 'wardrobe_dark', '옷장 · 흑단', { wood: 2 }),
  variant('wardrobe', 'wardrobe_oak', '옷장 · 참나무', { wood: 1 }),
  variant('wardrobe', 'wardrobe_narrow', '좁은 옷장', { w: 0.8 }),
  variant('old_sofa', 'old_sofa_leather', '소파 · 가죽', { cloth: 1 }),
  variant('old_sofa', 'old_sofa_red', '소파 · 붉은 천', { cloth: 2 }),
  variant('old_sofa', 'old_sofa_2', '2인 소파', { w: 1.4 }),
  variant('old_sofa', 'old_sofa_long', '긴 소파', { w: 2.3 }),
  variant('cushion', 'cushion_big', '큰 쿠션', { w: 0.6, d: 0.6, h: 0.14 }),
  variant('cushion', 'cushion_thin', '얇은 쿠션', { h: 0.07 }),
  variant('shelf_rack', 'shelf_rack_3', '철제 선반 3단', { shelves: 3, h: 1.5 }),
  variant('shelf_rack', 'shelf_rack_5', '철제 선반 5단', { shelves: 5, h: 2.1 }),
  variant('shelf_rack', 'shelf_rack_wide', '넓은 철제 선반', { w: 1.5 }),
  variant('shelf_rack', 'shelf_rack_low', '낮은 철제 선반', { shelves: 2, h: 1.0 }),
  variant('steel_cabinet', 'steel_cabinet_2', '서류함 2단', { n: 2, h: 0.75 }),
  variant('steel_cabinet', 'steel_cabinet_3', '서류함 3단', { n: 3, h: 1.05 }),
  variant('steel_cabinet', 'steel_cabinet_6', '서류함 6단', { n: 6, h: 1.85 }),
  variant('steel_cabinet', 'steel_cabinet_wide', '넓은 서류함', { w: 0.9 }),
  variant('crate', 'crate_small', '작은 나무 상자', { s: 0.45 }),
  variant('crate', 'crate_large', '큰 나무 상자', { s: 0.8 }),
  variant('product_box', 'product_box_small', '작은 택배 상자', { w: 0.24, h: 0.18, d: 0.24 }),
  variant('product_box', 'product_box_flat', '납작한 택배 상자', { w: 0.42, h: 0.14, d: 0.34 }),
  variant('product_box', 'product_box_tall', '길쭉한 택배 상자', { w: 0.3, h: 0.42, d: 0.3 }),
  variant('fridge', 'fridge_small', '작은 냉장고', { h: 1.0, freezer: 0.2 }),
  variant('fridge', 'fridge_tall', '키 큰 냉장고', { h: 1.8 }),
  variant('fridge', 'fridge_wide', '넓은 냉장고', { w: 0.72 }),
];
